import { CloseIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  AlertProps,
  Collapse,
  ColorProps,
  ComponentWithAs,
  IconButton,
  IconProps,
  Tooltip,
} from "@chakra-ui/react";
import { ExoticComponent } from "react";
import { FormattedMessage } from "react-intl";

import { useLocalStorage } from "@nens/react/hooks";

import { alertTheme } from "../theme/multipart-themes/alert";

import { RanaLoading } from "./rana-loading";

export interface RanaAlertProps extends Omit<AlertProps, "status"> {
  direction?: "horizontal" | "vertical";
  progress?: number;
  status?: AlertProps["status"] | "inProgress";
  iconSize?: AlertProps["boxSize"];
  overrideIcon?: ComponentWithAs<"svg", IconProps> | ExoticComponent;
  tooltipLabel?: string;
  onCloseKey?: string; // If provided, the alert will be closable and this key will be used to remember the closed state in localStorage
}

export const RanaAlert = ({
  direction = "horizontal",
  progress,
  iconSize = 9,
  status: _status,
  children,
  overrideIcon,
  onCloseKey,
  tooltipLabel,
  ...rest
}: RanaAlertProps) => {
  const inProgress = _status === "inProgress";
  const status = inProgress ? "info" : _status;
  const Icon = overrideIcon || AlertIcon;
  const [isClosed, setIsClosed] = useLocalStorage(onCloseKey || "", false);
  const iconColor =
    typeof alertTheme.baseStyle === "function"
      ? (alertTheme.baseStyle({ ...rest, status }).icon?.color as ColorProps["color"])
      : undefined;

  let alert = (
    <Alert
      alignItems="center"
      flexDirection={direction === "horizontal" ? "row" : "column"}
      {...rest}
      status={status}
    >
      {onCloseKey && (
        <Tooltip label={<FormattedMessage id="alert.close_alert" defaultMessage="Close alert" />}>
          <IconButton
            onClick={() => setIsClosed(true)}
            position="absolute"
            top={2}
            right={2}
            boxSize={3}
            minW="fit"
            variant="fourthiary"
            icon={<CloseIcon boxSize={3} />}
            aria-label="Close alert"
          />
        </Tooltip>
      )}

      {!overrideIcon && (status === "loading" || inProgress) ? (
        <RanaLoading progress={inProgress ? progress : undefined} boxSize={iconSize} m={0} />
      ) : (
        <Icon color={iconColor} boxSize={iconSize} m={0} />
      )}

      {children}
    </Alert>
  );

  if (onCloseKey) {
    alert = (
      <Collapse in={!isClosed} unmountOnExit>
        {alert}
      </Collapse>
    );
  }

  if (tooltipLabel) {
    alert = <Tooltip label={tooltipLabel}>{alert}</Tooltip>;
  }

  return alert;
};
